//@ts-strict-ignore
import { CellSortDirection } from './cells/headerCells/SortableHeaderCell';
import { Component, createRef, MutableRefObject, ReactElement } from 'react';
import { Maybe } from '../../graphql/schema/p4types';
import createObjectWithProperties from './createObjectWithProperties';

const React = require('react');
const joinClasses = require('classnames');
const shallowCloneObject = require('./shallowCloneObject');
const ColumnMetrics = require('./ColumnMetrics');
const ColumnUtils = require('./ColumnUtils');
const HeaderRow = require('./HeaderRow');
const getScrollbarSize = require('./getScrollbarSize');

require('styles/react-data-grid/react-data-grid-header.css');

type Column = {
  width: number;
  key?: string;
};

// The list of the propTypes that we want to include in the Header div
const knownDivPropertyKeys = ['height', 'onScroll'];

type IHeaderProps = {
  columnMetrics: {
    width: number;
    columns: any;
  };
  totalWidth: number | string;
  height: number;
  headerRows: Array<any>;
  sortColumn: string;
  sortDirection: CellSortDirection;
  onSort: Function;
  onColumnResize: Function;
  onScroll: Function;
  onHeaderDrop: Function;
  draggableHeaderCell: Function;
  getValidFilterValues: Function;
  includeHeaderRow?: boolean;
  cellMetaData: any; //TODO - Refactor shape(cellMetaDataShape),
};

class Header extends Component<IHeaderProps> {
  state: { resizing: any } = { resizing: null };
  private row: MutableRefObject<any>;
  private filterRow: MutableRefObject<any>;

  constructor(props: any, context: any) {
    super(props, context);

    this.row = createRef();
    this.filterRow = createRef();
  }

  // eslint-disable-next-line camelcase
  UNSAFE_componentWillReceiveProps(nextProps: any, nextContext: any) {
    this.setState({ resizing: null });
  }

  shouldComponentUpdate(nextProps: any, nextState: any): boolean {
    return (
      !ColumnMetrics.sameColumns(
        this.props.columnMetrics.columns,
        nextProps.columnMetrics.columns,
        ColumnMetrics.sameColumn
      ) ||
      this.props.totalWidth !== nextProps.totalWidth ||
      this.props.headerRows.length !== nextProps.headerRows.length ||
      this.state.resizing !== nextState.resizing ||
      this.props.sortColumn !== nextProps.sortColumn ||
      this.props.sortDirection !== nextProps.sortDirection
    );
  }

  onColumnResize = (column: Column, width: number) => {
    const state = this.state.resizing || this.props;

    const pos = this.getColumnPosition(column);

    if (pos !== null && pos !== undefined) {
      const resizing: { columnMetrics: any; column?: any } = {
        columnMetrics: shallowCloneObject(state.columnMetrics),
      };
      resizing.columnMetrics = ColumnMetrics.resizeColumn(resizing.columnMetrics, pos, width);

      // we don't want to influence scrollLeft while resizing
      if (resizing.columnMetrics.totalWidth < state.columnMetrics.totalWidth) {
        resizing.columnMetrics.totalWidth = state.columnMetrics.totalWidth;
      }

      resizing.column = ColumnUtils.getColumn(resizing.columnMetrics.columns, pos);
      this.setState({ resizing });
    }
  };

  onColumnResizeEnd = (column: Column, width: number) => {
    const pos = this.getColumnPosition(column);
    if (pos !== null && this.props.onColumnResize) {
      this.props.onColumnResize(pos, width || column.width);
    }
  };

  getHeaderRows = (): Array<ReactElement> => {
    const columnMetrics = this.getColumnMetrics();
    let resizeColumn;
    if (this.state.resizing) {
      resizeColumn = this.state.resizing.column;
    }
    const headerRows: Array<ReactElement> = [];
    this.props.headerRows.forEach((row, index) => {
      // To allow header filters to be visible
      let rowHeight = 'auto';
      if (row.rowType === 'filter') {
        rowHeight = '500px';
      }
      const scrollbarSize = getScrollbarSize() > 0 ? getScrollbarSize() : 0;
      const updatedWidth = isNaN((this.props.totalWidth as number) - scrollbarSize)
        ? this.props.totalWidth
        : (this.props.totalWidth as number) - scrollbarSize;
      const headerRowStyle = {
        position: 'absolute',
        top: this.getCombinedHeaderHeights(index),
        left: 0,
        width: updatedWidth,
        overflow: 'hidden',
        minHeight: rowHeight,
      };

      headerRows.push(
        <HeaderRow
          key={row.ref}
          ref={(node) => {
            return row.rowType === 'filter' ? (this.filterRow.current = node) : (this.row.current = node);
          }}
          rowType={row.rowType}
          style={headerRowStyle}
          onColumnResize={this.onColumnResize}
          onColumnResizeEnd={this.onColumnResizeEnd}
          width={columnMetrics.width}
          height={row.height || this.props.height}
          columns={columnMetrics.columns}
          resizing={resizeColumn}
          draggableHeaderCell={this.props.draggableHeaderCell}
          filterable={row.filterable}
          onFilterChange={row.onFilterChange}
          onHeaderDrop={this.props.onHeaderDrop}
          sortColumn={this.props.sortColumn}
          sortDirection={this.props.sortDirection}
          onSort={this.props.onSort}
          onScroll={this.props.onScroll}
          getValidFilterValues={this.props.getValidFilterValues}
        />
      );
    });
    return headerRows;
  };

  getColumnMetrics = () => {
    let columnMetrics;
    if (this.state.resizing) {
      columnMetrics = this.state.resizing.columnMetrics;
    } else {
      columnMetrics = this.props.columnMetrics;
    }
    return columnMetrics;
  };

  getColumnPosition = (column: Column): Maybe<number> => {
    const columnMetrics = this.getColumnMetrics();
    let pos = -1;
    columnMetrics.columns.forEach((c, idx) => {
      if (c.key === column.key) {
        pos = idx;
      }
    });
    return pos === -1 ? null : pos;
  };

  getCombinedHeaderHeights = (until?: number): number => {
    let stopAt = this.props.headerRows.length;
    if (typeof until !== 'undefined') {
      stopAt = until;
    }

    let height = 0;
    for (let index = 0; index < stopAt; index++) {
      height += this.props.headerRows[index].height || this.props.height;
    }
    return height;
  };

  getStyle = (): { position: string; height: number } => {
    return {
      position: 'relative',
      height: this.props.includeHeaderRow ? this.getCombinedHeaderHeights() + 2 : 0,
    };
  };

  setScrollLeft = (scrollLeft: number) => {
    const node = this.row.current;
    if (node) {
      node.scrollLeft = scrollLeft;
      node.setScrollLeft?.(scrollLeft);
    }
    const nodeFilters = this.filterRow.current;
    if (nodeFilters) {
      nodeFilters.scrollLeft = scrollLeft;
      nodeFilters.setScrollLeft?.(scrollLeft);
    }
  };

  getKnownDivProps = () => {
    return createObjectWithProperties(this.props, knownDivPropertyKeys);
  };

  // Set the cell selection to -1 x -1 when clicking on the header
  onHeaderClick = (e) => {
    this.props.cellMetaData.onCellClick({ rowIdx: -1, idx: -1 }, e);
  };

  render() {
    const className = joinClasses({
      'react-grid-Header': true,
      'react-grid-Header--resizing': !!this.state.resizing,
    });
    const headerRows = this.getHeaderRows();

    return (
      <div {...this.getKnownDivProps()} style={this.getStyle()} className={className} onClick={this.onHeaderClick}>
        {headerRows}
      </div>
    );
  }
}

export default Header;
